import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import portalApi from '../../../../lib/portalApi';
import { Building2, Search, Filter } from 'lucide-react';

export default function MentorStartupsPage() {
  const navigate = useNavigate();
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');

  const loadStartups = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (stageFilter) params.stage = stageFilter;
      if (batchFilter) params.batch = batchFilter;
      const res = await portalApi.getMentorStartups(params);
      setStartups(res?.startups || []);
    } catch (err) {
      console.error('Failed to load startups:', err);
      setError(err.message || 'Failed to load assigned startups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStartups();
  }, [stageFilter, batchFilter]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadStartups();
  };

  const formatMeetingDate = (dateStr) => {
    if (!dateStr) return 'No upcoming meeting';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Top Header matching PDF Page 4 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-extrabold text-[#F2F2F4] tracking-tight">Startups</h1>
            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-[#321129] text-[#D42B92] border border-[#D42B92]/30">
              {startups.length} assigned to you
            </span>
          </div>
          <p className="text-xs text-[#8E8E99] mt-0.5">
            Click any startup to review its documents, meetings, tasks and deadlines.
          </p>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-wrap items-center gap-2.5">
          <form onSubmit={handleSearchSubmit} className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E99]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search startup..."
              className="pl-8 pr-3 py-2 rounded-xl bg-[#131318] border border-[#2C2C34] text-xs text-[#F2F2F4] placeholder-[#8E8E99] focus:outline-none focus:border-[#D42B92] w-48 sm:w-56"
            />
          </form>

          {/* Stage Filter */}
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="px-3 py-2 rounded-xl bg-[#131318] border border-[#2C2C34] text-xs text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
          >
            <option value="">All Stages</option>
            <option value="active_batch">Active Batch</option>
            <option value="accepted">Accepted</option>
            <option value="demo_day">Demo Day</option>
            <option value="graduated">Graduated</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* 6-Column Table matching PDF Page 4 */}
      <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl overflow-hidden shadow-xl">
        {loading ? (
          <div className="p-12 text-center text-xs text-[#8E8E99]">Loading startups…</div>
        ) : startups.length === 0 ? (
          <div className="p-12 text-center space-y-2">
            <Building2 className="w-8 h-8 text-[#8E8E99] mx-auto opacity-40" />
            <div className="text-sm font-medium text-[#F2F2F4]">
              {search || stageFilter
                ? 'No startups found matching your filter.'
                : 'No startups assigned yet. Please contact the admin.'}
            </div>
            {(search || stageFilter) && (
              <button
                onClick={() => {
                  setSearch('');
                  setStageFilter('');
                }}
                className="text-xs text-[#D42B92] hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="bg-[#08080A]/60 text-[#8E8E99] border-b border-[#2C2C34]">
                  <th className="py-3.5 px-5 font-semibold uppercase tracking-wider">STARTUP</th>
                  <th className="py-3.5 px-4 font-semibold uppercase tracking-wider">FOUNDER</th>
                  <th className="py-3.5 px-4 font-semibold uppercase tracking-wider">BATCH</th>
                  <th className="py-3.5 px-4 font-semibold uppercase tracking-wider">STAGE</th>
                  <th className="py-3.5 px-4 font-semibold uppercase tracking-wider">PROGRESS</th>
                  <th className="py-3.5 px-5 font-semibold uppercase tracking-wider text-right">NEXT MEETING</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2C2C34]/40">
                {startups.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => navigate(`/mentor-dashboard/startup/${s.id}`)}
                    className="hover:bg-white/[0.02] cursor-pointer transition-colors group"
                  >
                    {/* Column 1: Startup Name + Logo */}
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-3">
                        {s.logo ? (
                          <img
                            src={s.logo}
                            alt=""
                            className="w-8 h-8 rounded-lg object-cover border border-[#2C2C34]"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-[#321129] border border-[#D42B92]/30 flex items-center justify-center text-xs font-bold text-[#D42B92]">
                            {s.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="font-semibold text-sm text-[#F2F2F4] group-hover:text-[#D42B92] transition-colors">
                            {s.name}
                          </div>
                          {s.industry && (
                            <div className="text-[11px] text-[#8E8E99]">{s.industry}</div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Column 2: Founder */}
                    <td className="py-4 px-4 text-[#F2F2F4]">
                      <div>{s.founder}</div>
                      {s.founderEmail && (
                        <div className="text-[11px] text-[#8E8E99] truncate max-w-[140px]">{s.founderEmail}</div>
                      )}
                    </td>

                    {/* Column 3: Batch */}
                    <td className="py-4 px-4 text-[#8E8E99] font-medium">
                      {s.batch}
                    </td>

                    {/* Column 4: Current Stage */}
                    <td className="py-4 px-4">
                      <span className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-white/[0.04] text-[#F2F2F4] border border-[#2C2C34]">
                        {s.stage}
                      </span>
                    </td>

                    {/* Column 5: Progress % */}
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-[#2C2C34] h-1.5 rounded-full overflow-hidden">
                          <div
                            className="bg-[#D42B92] h-full rounded-full"
                            style={{ width: `${s.progress}%` }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-[#F2F2F4]">{s.progress}%</span>
                      </div>
                    </td>

                    {/* Column 6: Next Meeting */}
                    <td className="py-4 px-5 text-right font-medium text-[#F2F2F4]">
                      {formatMeetingDate(s.nextMeeting)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
